import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import format from 'string-template';
import * as Sentry from '@sentry/browser';

import Workout from '../../../../../../Model/Workout';
import FileSelectButton from '../../../../../../components/FileSelectButton';
import getSessionVideoFileError from '../../../../../../components/SessionVideoUpload/getSessionVideoFileError';
import SessionVideoUploadStatus from '../../../../../../components/SessionVideoUpload/SessionVideoUploadStatus';
import sessionVideoTexts from '../../../../../../components/SessionVideoUpload/texts.json';
import LongSessionUploadContext, {
  LongSessionUploadActionsContext,
  UploadKind,
  UploadState,
} from '../../../../../../context/LongSessionUploadContext';
import useStorage from '../../../../../../hooks/useStorage';
import { formatDuration, TimeDurationFormat } from '../../../../../../utils/time';
import { transformVideoUrl } from '../../../../../../utils/video';
import {
  WorkoutPublishStatus,
  getLongSessionSourcePath,
  isUploadInterrupted,
} from '../../../../../../utils/longSession';
import {
  Container,
  Title,
  Description,
  StatusCard,
  StatusRow,
  HelperText,
  ErrorText,
  SelectButton,
  ReadyPreview,
  SessionVideo,
  ReadyDetails,
} from '../../../../../../components/SessionVideoUpload/styles';

import texts from './texts.json';

/**
 * The long-format session's half of the workout builder: one video in place of the exercise table.
 *
 * The workout is created the moment a file is chosen, not on save. That ordering is what removes the
 * draft problem entirely — there is no unsaved state to lose, because by the time bytes are moving
 * the document already exists and carries the status. Everything past UPLOADING is written by the
 * server, and this panel is a read-only observer of it.
 */
const LongSessionPanel = ({
  workoutDoc,
  coachId,
  workoutName,
  workoutNote,
  onWorkoutCreated,
}) => {
  const { uploads } = useContext(LongSessionUploadContext);
  const { startUpload } = useContext(LongSessionUploadActionsContext);
  const { deleteAttachment } = useStorage();
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const workoutId = workoutDoc?.id;
  const stage = workoutDoc?.videoProcessing?.stage;
  const publishStatus = workoutDoc?.publishStatus;
  const upload = uploads[workoutId];
  const hasLiveUpload = upload?.state === UploadState.UPLOADING;
  /*
    A finished upload keeps its entry until the server moves the stage, so it is never mistaken for
    one whose bytes died with a closed tab.
  */
  const canResume = !!workoutId && isUploadInterrupted({ publishStatus, stage, upload });
  /*
    Only while the session is still waiting for a video: the server deletes a finalized object
    unless the workout is PENDING at stage UPLOADING, so a replace offered any later would lose
    the new upload after the original was already gone.
  */
  const acceptsNewVideo = !workoutId
    || canResume
    || publishStatus === WorkoutPublishStatus.FAILED;

  const handleFileSelected = useCallback(async (event) => {
    const [file] = event.target.files || [];
    if (!file) {
      return;
    }

    const validationError = getSessionVideoFileError(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');

    let sessionWorkout = workoutDoc;
    let storagePath;

    setIsCreating(true);
    try {
      // First video for this session: the workout has to exist before the first byte is sent.
      const isNewWorkout = !sessionWorkout;
      if (isNewWorkout) {
        const newWorkoutId = await Workout.addPendingLongSessionWorkout({
          coachId,
          name: workoutName,
          note: workoutNote,
        });
        sessionWorkout = await Workout.getWorkoutById(newWorkoutId);
      }

      // The path contains the workout id, so it can only be settled once the workout exists.
      storagePath = getLongSessionSourcePath(coachId, sessionWorkout.id, file.name);
      const previousPath = sessionWorkout.sourceStoragePath;

      /*
        Resuming or replacing with a differently named file: the old object would otherwise sit in
        the folder forever, since nothing would hold a reference to it any more. A new workout has no
        previous path, so this is skipped and the write below simply records the first one.
      */
      if (previousPath && previousPath !== storagePath) {
        await deleteAttachment(previousPath, { ignoreNotFound: true });
      }
      if (isNewWorkout) {
        // Born PENDING at stage UPLOADING, so the path is the only thing still missing.
        await sessionWorkout.updateFields({ sourceStoragePath: storagePath });
        onWorkoutCreated(sessionWorkout);
      } else {
        // Unconditional: retrying a failure with the same file still has to leave FAILED behind.
        await sessionWorkout.startLongSessionUpload(storagePath);
      }
    } catch (createError) {
      Sentry.captureException(createError, {
        extra: { message: 'Failed to prepare a long-format session upload', coachId },
      });
      setError(texts.createFailed);
      return;
    } finally {
      setIsCreating(false);
    }

    const sessionWorkoutId = sessionWorkout.id;
    await startUpload({
      key: sessionWorkoutId,
      kind: UploadKind.WORKOUT,
      coachId,
      storagePath,
      file,
      onStopped: (stopped) => Workout.markLongSessionUploadStopped(sessionWorkoutId, stopped),
    });
  }, [
    workoutDoc,
    coachId,
    workoutName,
    workoutNote,
    onWorkoutCreated,
    startUpload,
    deleteAttachment,
  ]);

  const renderStatus = () => {
    if (publishStatus === WorkoutPublishStatus.READY) {
      const [sessionActivity] = workoutDoc?.activities || [];
      const playableUrl = sessionActivity?.videoUrl || sessionActivity?.videoPreviewUrl;
      const { width, height } = sessionActivity?.video || {};

      return (
        <StatusCard>
          <StatusRow>{texts.ready}</StatusRow>
          {/*
            The finished video, playable, not just a label. This is the coach's only confirmation
            that what came back out of processing is the session they uploaded — and the only place
            they can check it before a client ever sees it.
          */}
          <ReadyPreview>
            {!!playableUrl && (
              <SessionVideo
                src={transformVideoUrl(playableUrl)}
                title={sessionActivity.name || workoutName}
                // Vimeo's own dimensions when we have them; 16:9 is only a fallback.
                $aspectRatio={width && height ? `${width} / ${height}` : '16 / 9'}
                allow="fullscreen; picture-in-picture"
                allowFullScreen
              />
            )}
            <ReadyDetails>
              <span>{sessionActivity?.name || workoutName}</span>
              {!!sessionActivity?.duration && (
                <span>
                  {format(texts.readyDuration, {
                    duration: formatDuration(sessionActivity.duration, TimeDurationFormat.CLOCK_FORMAT),
                  })}
                </span>
              )}
            </ReadyDetails>
          </ReadyPreview>
        </StatusCard>
      );
    }

    return (
      <SessionVideoUploadStatus
        publishStatus={publishStatus}
        videoProcessing={workoutDoc?.videoProcessing}
        upload={upload}
        canResume={canResume}
        copy={texts.status}
      />
    );
  };

  const selectLabel = workoutId ? texts.replaceFile : texts.selectFile;
  // A name is what the workout is created with, so it has to come first.
  const canSelectFile = !!workoutName && !isCreating;

  return (
    <Container>
      <Title>{texts.title}</Title>
      <Description>{texts.description}</Description>
      {!!workoutId && renderStatus()}
      {!hasLiveUpload && acceptsNewVideo && (
        <FileSelectButton
          ButtonComponent={SelectButton}
          onSelect={handleFileSelected}
          disabled={!canSelectFile}
          multiple={false}
          extraInputProps={{ accept: 'video/*' }}
        >
          {canResume ? sessionVideoTexts.sendAgain : selectLabel}
        </FileSelectButton>
      )}
      {!workoutName && <HelperText>{texts.nameRequired}</HelperText>}
      {!!error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

LongSessionPanel.propTypes = {
  /** The session's workout, once it exists. Null until the coach picks the first video. */
  workoutDoc: PropTypes.object,
  coachId: PropTypes.string.isRequired,
  workoutName: PropTypes.string,
  workoutNote: PropTypes.string,
  onWorkoutCreated: PropTypes.func.isRequired,
};

LongSessionPanel.defaultProps = {
  workoutDoc: null,
  workoutName: '',
  workoutNote: '',
};

export default observer(LongSessionPanel);
